import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getVanillaEl } from "../../domBridge";
import { useActivityCards } from "./useActivityCards";
import { ActivityCard } from "./ActivityCard";

// Portals into #activityCarouselAnchor. A horizontally-swipeable,
// scroll-snap row of the 4 activity cards (Watch/Play/Listen Together,
// Gift), already sorted by priority (useActivityCards.ts) so an incoming
// invite or ready-to-start session surfaces first. The native scrollbar is
// hidden (styles.css) in favor of the small pagination dots below, which
// track whichever card is currently snapped into view.
export function ActivityCarousel() {
  const cards = useActivityCards();
  const trackRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const rafRef = useRef<number | null>(null);

  // Finds whichever item's offsetLeft is closest to the track's current
  // scrollLeft - robust to the exact flex-basis/gap values (unlike
  // dividing scrollLeft by a fixed item width), and naturally lands on
  // whatever card scroll-snap actually settled on.
  const handleScroll = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const track = trackRef.current;
      if (!track) return;
      let closest = 0;
      let closestDist = Infinity;
      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        const dist = Math.abs(el.offsetLeft - track.scrollLeft);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });
      setActiveIndex(closest);
    });
  }, []);

  function goToCard(index: number): void {
    const el = itemRefs.current[index];
    el?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  }

  const anchor = getVanillaEl<HTMLDivElement>("activityCarouselAnchor");
  if (!anchor) return null;

  return createPortal(
    <>
      <div className="home-section-heading">
        <h2 className="home-section-title">Things to do together</h2>
        <span className="home-section-hint">
          Swipe to explore <i className="bi bi-arrow-right" aria-hidden="true" />
        </span>
      </div>
      <div className="activity-carousel" ref={trackRef} onScroll={handleScroll} role="list" aria-label="Activities">
        {cards.map((card, i) => (
          <div
            className="activity-carousel-item"
            role="listitem"
            key={card.id}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
          >
            <ActivityCard config={card} />
          </div>
        ))}
      </div>
      {cards.length > 1 && (
        <div className="activity-carousel-dots" role="tablist" aria-label="Activity carousel position">
          {cards.map((card, i) => (
            <button
              key={card.id}
              type="button"
              className={`activity-carousel-dot${i === activeIndex ? " is-active" : ""}`}
              role="tab"
              aria-selected={i === activeIndex}
              aria-label={`Show ${card.title}`}
              onClick={() => goToCard(i)}
            />
          ))}
        </div>
      )}
    </>,
    anchor,
  );
}
